<template>
  <div :class="['docx-entry-page', { 'is-embedded': embedded }]">
    <div class="docx-entry-toolbar">
      <template v-if="showBatchToolbar">
        <template v-for="item in batchToolbarItems">
          <span :key="item.key + '-label'" class="toolbar-label">{{ item.label }}</span>
          <el-select
            v-if="item.type === 'instrument'"
            :key="item.key + '-select'"
            :value="instrumentSelectValue(item)"
            filterable
            clearable
            :placeholder="item.category === 'balance' ? '请选择天平' : '请选择仪器'"
            size="small"
            class="toolbar-select"
            :disabled="applying"
            @change="val => onToolbarInstrumentChange(item, val)"
          >
            <el-option
              v-for="inst in instrumentsForItem(item)"
              :key="inst.code || inst.name"
              :label="instrumentOptionLabel(inst)"
              :value="inst.code || inst.name"
            >
              <span class="inst-opt-name">{{ inst.name }}</span>
              <span
                v-if="inst.showCodeInLabel && inst.code && !String(inst.name || '').includes(inst.code)"
                class="inst-opt-code"
              > · {{ inst.code }}</span>
            </el-option>
          </el-select>
          <el-date-picker
            v-else-if="item.type === 'date'"
            :key="item.key + '-date'"
            v-model="batchFields[item.key]"
            type="date"
            size="small"
            class="toolbar-date"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            placeholder="选择日期"
            :disabled="applying"
            :clearable="false"
            @change="val => onToolbarDateChange(item, val)"
          />
        </template>
      </template>
      <div class="toolbar-actions">
        <el-button
          size="small"
          type="danger"
          plain
          class="toolbar-clear"
          :loading="clearLoading"
          :disabled="!ooConfig && !ooLoading"
          @click="clearFormData"
        >
          清除数据并重填
        </el-button>
        <el-button
          size="small"
          type="primary"
          plain
          class="toolbar-reload"
          :loading="reloadLoading"
          :disabled="!ooConfig && !ooLoading"
          @click="reloadEditor"
        >
          重新加载
        </el-button>
      </div>
    </div>
    <OnlyOfficeEditor
      v-if="ooConfig"
      ref="ooEditor"
      :key="ooEditorKey"
      :config="ooConfig"
      :fullscreen="true"
      class="docx-entry-editor"
      @ready="onOoDocumentReady"
      @saved="onWordSaved"
    />
    <div v-else-if="ooLoading" class="docx-entry-placeholder">正在打开 Word 原表…</div>
    <div v-else-if="loadError" class="docx-entry-placeholder is-error">
      {{ loadError }}
      <el-button size="small" style="margin-top:12px" @click="loadEditor">重试</el-button>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'
import OnlyOfficeEditor from '@/components/OnlyOfficeEditor.vue'
import { getCurrentUser, inheritSessionForPopup } from '@/utils/user'
import { instrumentSiblingKeys, balanceSiblingKeys } from '@/utils/lab_form_engine'
import labInstruments from '../../../config/lab_instruments.json'
import formDocxToolbar from '../../../config/form_docx_toolbar.json'

export default {
  name: 'SoilDocxEntry',
  components: { OnlyOfficeEditor },
  props: {
    /** 嵌入土壤录入页全屏层时使用，避免新开窗口暴露长 URL */
    embeddedQuery: { type: Object, default: null },
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      ooConfig: null,
      ooLoading: false,
      ooEditorKey: '',
      loadError: '',
      saveSyncTimer: null,
      instrumentLists: {},
      instrumentCategories: (labInstruments && labInstruments.categories) || [],
      batchFields: {},
      applying: false,
      reloadLoading: false,
      clearLoading: false,
      headerSynced: false,
      headerSyncing: false,
    }
  },
  computed: {
    routeQuery() {
      return this.embeddedQuery || this.$route.query || {}
    },
    entryIdentity() {
      const q = this.routeQuery
      return [q.xmbh, q.form_num, q.scope, q.ntr_id, q.ypbh].map(v => String(v || '')).join('|')
    },
    xmbh() {
      return String(this.routeQuery.xmbh || '')
    },
    formNum() {
      const raw = String(this.routeQuery.form_num || '')
      const m = raw.match(/^(\d+)-1$/)
      return m ? m[1] : raw.replace(/\D/g, '')
    },
    formNumDisplay() {
      return String(this.routeQuery.form_num || this.formNum || '')
    },
    isContinuationForm() {
      return /^\d+-\d+$/.test(String(this.routeQuery.form_num || ''))
    },
    scope() {
      return this.routeQuery.scope === 'sample' ? 'sample' : 'batch'
    },
    ntrId() {
      return String(this.routeQuery.ntr_id || '')
    },
    ypbh() {
      return String(this.routeQuery.ypbh || '')
    },
    pageTitle() {
      const num = this.formNumDisplay || this.formNum || '—'
      const mc = String(this.routeQuery.form_mc || '')
      return mc ? `GSYN-${num} ${mc}.docx` : `GSYN-${num}.docx`
    },
    toolbarCfg() {
      const forms = (formDocxToolbar && formDocxToolbar.forms) || {}
      const fd = this.formNumDisplay || this.formNum
      return forms[fd] || forms[this.formNum] || {}
    },
    batchToolbarItems() {
      if (this.scope !== 'batch') return []
      return this.toolbarCfg.batchToolbar || []
    },
    showBatchToolbar() {
      return this.batchToolbarItems.length > 0
    }
  },
  mounted() {
    this.bootstrapEntry()
  },
  watch: {
    entryIdentity(next, prev) {
      if (!prev || next === prev) return
      this.bootstrapEntry()
    }
  },
  beforeDestroy() {
    if (this.saveSyncTimer) clearTimeout(this.saveSyncTimer)
  },
  methods: {
    bootstrapEntry() {
      if (!this.embedded) document.title = this.pageTitle
      inheritSessionForPopup()
      this.ooConfig = null
      this.ooLoading = false
      this.ooEditorKey = ''
      this.loadError = ''
      this.applying = false
      this.reloadLoading = false
      this.clearLoading = false
      this.headerSynced = false
      this.headerSyncing = false
      if (!this.xmbh || !this.formNum) {
        this.loadError = '缺少项目或表单参数，请从土壤检测页重新打开'
        return
      }
      this.initBatchFields()
      this.loadAllInstruments().finally(() => {
        this.loadBatchFields().finally(() => this.loadEditor())
      })
    },
    formatToday() {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    initBatchFields() {
      const fields = {}
      this.batchToolbarItems.forEach(item => {
        fields[item.key] = ''
        if (item.type === 'instrument' && item.siblings) {
          Object.values(item.siblings).forEach(k => { fields[k] = '' })
        }
      })
      this.batchFields = fields
    },
    instrumentsForItem(item) {
      const cat = item.category || this.toolbarCfg.category || 'other'
      const list = this.instrumentLists[cat] && this.instrumentLists[cat].length
        ? this.instrumentLists[cat]
        : ((this.instrumentCategories.find(c => c.id === cat) || {}).instruments || [])
      const filtered = list.filter(i => !this.isTestInstrumentName(i.name) && !this.isTestInstrumentName(i.code))
      const nameCounts = {}
      filtered.forEach(i => {
        const n = String((i && i.name) || '').trim()
        if (n) nameCounts[n] = (nameCounts[n] || 0) + 1
      })
      return filtered.map(i => {
        const n = String((i && i.name) || '').trim()
        return { ...i, showCodeInLabel: (nameCounts[n] || 0) > 1 }
      })
    },
    instrumentOptionLabel(inst) {
      const name = String((inst && inst.name) || '').trim()
      const code = String((inst && inst.code) || '').trim()
      if (!name) return code
      if (!code || name.includes(code)) return name
      if (inst.showCodeInLabel) return `${name} · ${code}`
      return name
    },
    instrumentSelectValue(item) {
      const sib = item.siblings || balanceSiblingKeys(item.key) || instrumentSiblingKeys(item.key) || {}
      const code = sib.code ? String(this.batchFields[sib.code] || '').trim() : ''
      if (code) return code
      const name = String(this.batchFields[item.key] || '').trim()
      if (!name) return ''
      const list = this.instrumentsForItem(item)
      const matched = list.filter(i => i.name === name)
      if (matched.length === 1) return matched[0].code || matched[0].name
      return name
    },
    isTestInstrumentName(val) {
      const s = String(val || '').trim()
      if (!s) return false
      if (s.includes('【测试】')) return true
      if (/^TEST-/i.test(s)) return true
      if (/^GC-\d+$/i.test(s)) return true
      if (/Agilent/i.test(s)) return true
      return false
    },
    async loadAllInstruments() {
      if (!this.showBatchToolbar) return
      const cats = [...new Set(
        this.batchToolbarItems
          .filter(it => it.type === 'instrument')
          .map(it => it.category || this.toolbarCfg.category || 'other')
      )]
      await Promise.all(cats.map(async cat => {
        let list = []
        try {
          const res = await api.get('yn_tr_instruments', { category: cat })
          list = ((res.data || {}).instruments) || []
        } catch (e) { /* 回退静态 JSON */ }
        if (!list.length) {
          list = ((this.instrumentCategories.find(c => c.id === cat) || {}).instruments) || []
        }
        if (list.length) this.$set(this.instrumentLists, cat, list)
      }))
    },
    async loadBatchFields() {
      if (!this.showBatchToolbar) return
      try {
        const res = await api.get('yn_tr_lab_batch', { xmbh: this.xmbh })
        const row = ((res.data || {}).batchByForm || {})[this.formNum] || {}
        this.batchToolbarItems.forEach(item => {
          this.$set(this.batchFields, item.key, row[item.key] || '')
          if (item.type === 'instrument') {
            const sib = item.siblings || balanceSiblingKeys(item.key) || instrumentSiblingKeys(item.key) || {}
            if (sib.code) this.$set(this.batchFields, sib.code, row[sib.code] || '')
            if (sib.trace) this.$set(this.batchFields, sib.trace, row[sib.trace] || '')
            if (sib.expiry) this.$set(this.batchFields, sib.expiry, row[sib.expiry] || '')
          }
        })
        const dateItem = this.batchToolbarItems.find(it => it.type === 'date')
        if (dateItem) {
          const today = this.formatToday()
          if (row[dateItem.key]) {
            this.$set(this.batchFields, dateItem.key, row[dateItem.key])
          } else {
            this.$set(this.batchFields, dateItem.key, today)
            await this.persistBatchFields({ [dateItem.key]: today })
          }
        }
      } catch (e) { /* ignore */ }
    },
    async loadEditor(options = {}) {
      if (!this.xmbh || !this.formNum) return null
      const soft = !!(options && options.soft)
      const u = getCurrentUser() || {}
      if (!soft) {
        this.ooLoading = true
        this.ooConfig = null
      }
      this.loadError = ''
      try {
        const params = {
          xmbh: this.xmbh,
          form_num: this.formNumDisplay || this.formNum,
          scope: this.scope,
          yhid: this.routeQuery.yhid || u.id || '',
          yhmc: this.routeQuery.yhmc || u.xm || u.dlh || ''
        }
        if (this.scope === 'sample') {
          params.ntr_id = this.ntrId
          params.ypbh = this.ypbh
        }
        const res = await api.get('yn_tr_lab_oo_open', params)
        const payload = res.data || {}
        if (payload.error) {
          this.loadError = payload.error
          return null
        }
        if (this.isContinuationForm && payload.document) {
          payload.document.title = `GSYN-${this.formNumDisplay} 续表.docx`
        }
        if (!payload.document || !payload.document.url) {
          this.loadError = '接口返回异常，请从土壤检测页重新打开'
          return null
        }
        this.ooConfig = payload
        if (!soft) {
          this.ooEditorKey = `${this.formNum}-${this.scope}-${this.ntrId}-${Date.now()}`
        }
        return payload
      } catch (e) {
        const msg = (e && e.message) || ''
        if (/Network Error|timeout|ECONNREFUSED/i.test(msg)) {
          this.loadError = '无法连接服务器，请确认网络或本地 dev 代理正常后重试'
        } else {
          this.loadError = msg ? `打开 Word 原表失败：${msg}` : '打开 Word 原表失败'
        }
        return null
      } finally {
        this.ooLoading = false
      }
    },
    async reloadEditor() {
      if (this.reloadLoading) return
      this.reloadLoading = true
      this.headerSynced = false
      try {
        const ok = await this.mergeFieldsToWord(this.batchFields, { silent: true, allowReload: true })
        if (ok) {
          this.headerSynced = true
          this.$message({
            message: '已写入 Word',
            type: 'success',
            duration: 1500,
            showClose: false
          })
          return
        }
        const editor = this.$refs.ooEditor
        if (editor) editor.destroyEditor()
        const payload = await this.fetchOpenConfig()
        if (!payload) return
        this.ooConfig = null
        await this.$nextTick()
        this.ooConfig = payload
        this.ooEditorKey = `${this.formNum}-${this.scope}-${this.ntrId}-${Date.now()}`
        this.$message({ message: '已重新加载 Word 原表', type: 'success', duration: 1200, showClose: false })
      } finally {
        this.reloadLoading = false
      }
    },
    async clearFormData() {
      if (this.clearLoading) return
      try {
        await this.$confirm(
          '将清空本表在系统中已保存的数据，并从模板重新生成 Word。是否继续？',
          '清除数据并重填',
          { type: 'warning', confirmButtonText: '清除并重填', cancelButtonText: '取消' }
        )
      } catch (e) {
        return
      }
      this.clearLoading = true
      try {
        const editor = this.$refs.ooEditor
        if (editor) editor.destroyEditor()
        const res = await api.post('yn_tr_lab_oo_clear_form', {
          xmbh: this.xmbh,
          form_num: this.formNumDisplay || this.formNum,
          scope: this.scope,
          ntr_id: this.ntrId
        })
        const payload = res.data || {}
        if (payload.error) {
          this.$message.error(payload.error)
          return
        }
        this.ooConfig = null
        await this.$nextTick()
        await this.loadEditor()
        this.notifyOpener()
        this.$message({ message: '已清除数据并从模板重新生成', type: 'success', duration: 2000, showClose: false })
      } catch (e) {
        this.$message.error((e && e.message) || '清除失败')
      } finally {
        this.clearLoading = false
      }
    },
    async fetchOpenConfig() {
      if (!this.xmbh || !this.formNum) return null
      const u = getCurrentUser() || {}
      const params = {
        xmbh: this.xmbh,
        form_num: this.formNumDisplay || this.formNum,
        scope: this.scope,
        yhid: this.$route.query.yhid || u.id || '',
        yhmc: this.$route.query.yhmc || u.xm || u.dlh || ''
      }
      if (this.scope === 'sample') {
        params.ntr_id = this.ntrId
        params.ypbh = this.ypbh
      }
      try {
        const res = await api.get('yn_tr_lab_oo_open', params)
        const payload = res.data || {}
        if (payload.error) {
          this.$message.error(payload.error)
          return null
        }
        if (!payload.document || !payload.document.url) {
          this.$message.error('接口返回异常')
          return null
        }
        return payload
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
        return null
      }
    },
    onToolbarInstrumentChange(item, codeOrName) {
      if (this.applying) return
      const list = this.instrumentsForItem(item)
      const inst = list.find(i => (i.code || i.name) === codeOrName)
        || list.find(i => i.name === codeOrName)
      const sib = item.siblings || balanceSiblingKeys(item.key) || instrumentSiblingKeys(item.key) || {}
      const name = inst ? inst.name : (codeOrName || '')
      const fields = { [item.key]: name }
      this.$set(this.batchFields, item.key, name)
      if (inst) {
        if (sib.code) fields[sib.code] = inst.code || ''
        if (sib.expiry) fields[sib.expiry] = inst.traceExpiry || ''
        if (sib.trace) fields[sib.trace] = inst.traceMethod || ''
        if (sib.code) this.$set(this.batchFields, sib.code, inst.code || '')
        if (sib.expiry) this.$set(this.batchFields, sib.expiry, inst.traceExpiry || '')
        if (sib.trace) this.$set(this.batchFields, sib.trace, inst.traceMethod || '')
      } else {
        if (sib.code) { fields[sib.code] = ''; this.$set(this.batchFields, sib.code, '') }
        if (sib.expiry) { fields[sib.expiry] = ''; this.$set(this.batchFields, sib.expiry, '') }
        if (sib.trace) { fields[sib.trace] = ''; this.$set(this.batchFields, sib.trace, '') }
      }
      this.mergeFieldsToWord(fields, { silent: true })
    },
    onToolbarDateChange(item, val) {
      if (this.applying) return
      const date = val || this.formatToday()
      this.$set(this.batchFields, item.key, date)
      this.mergeFieldsToWord({ [item.key]: date }, { silent: true })
    },
    mergeLabelStopList() {
      const mergeLabels = (this.toolbarCfg && this.toolbarCfg.mergeLabels) || {}
      const labels = Object.values(mergeLabels).filter(l => l && !String(l).startsWith('__'))
      ;['分析项目', '方法依据', '温湿度', '仪器溯源方式', '检验类别', '检测项目'].forEach(l => {
        if (!labels.includes(l)) labels.push(l)
      })
      return labels
    },
    buildMergeLabelItems(source) {
      const mergeLabels = (this.toolbarCfg && this.toolbarCfg.mergeLabels) || {}
      const data = source || this.batchFields || {}
      const allLabels = this.mergeLabelStopList()
      const items = []
      Object.keys(mergeLabels).forEach(key => {
        if (String(key).startsWith('__')) return
        const val = data[key]
        if (val === undefined || val === null || String(val).trim() === '') return
        items.push({
          label: mergeLabels[key],
          value: String(val),
          allLabels
        })
      })
      return items
    },
    async persistBatchFields(fields) {
      const patch = fields || {}
      Object.keys(patch).forEach(k => {
        this.$set(this.batchFields, k, patch[k])
      })
      const merged = { ...this.batchFields }
      const res = await api.post('yn_tr_lab_oo_apply_fields', {
        xmbh: this.xmbh,
        form_num: this.formNumDisplay || this.formNum,
        scope: this.scope,
        ntr_id: this.ntrId,
        skip_docx: 1,
        fields: JSON.stringify(merged)
      })
      const payload = res.data || {}
      if (payload.error) {
        return { ok: false, error: payload.error }
      }
      return { ok: true }
    },
    async writeLabelsViaConnector(source) {
      const items = this.buildMergeLabelItems(source)
      if (!items.length) return true
      const ed = this.$refs.ooEditor
      if (!ed || typeof ed.applyMergeLabels !== 'function') return false
      const ok = await ed.applyMergeLabels(items)
      if (ok && ed.triggerForceSave) {
        setTimeout(() => ed.triggerForceSave(), 400)
      }
      return ok
    },
    async mergeFieldsToWord(fields, options = {}) {
      const silent = !!(options && options.silent)
      const allowReload = !!options.allowReload
      const patch = fields || {}
      Object.keys(patch).forEach(k => {
        this.$set(this.batchFields, k, patch[k])
      })
      const merged = { ...this.batchFields }
      const viaConnector = await this.writeLabelsViaConnector(merged)
      if (viaConnector) {
        const saved = await this.persistBatchFields({})
        if (!saved.ok) {
          if (!silent) this.$message.error(saved.error || '保存失败')
          return false
        }
        return true
      }
      const saved = await this.persistBatchFields({})
      if (!saved.ok) {
        if (!silent) this.$message.error(saved.error || '保存失败')
        return false
      }
      if (!allowReload) {
        if (!silent) {
          this.$message({
            message: '表头已保存，若 Word 未显示请点「重新加载」',
            type: 'info',
            duration: 2000,
            showClose: false
          })
        }
        return true
      }
      const ed = this.$refs.ooEditor
      if (ed && typeof ed.prepareForServerMerge === 'function') {
        await ed.prepareForServerMerge()
      }
      const res = await api.post('yn_tr_lab_oo_apply_fields', {
        xmbh: this.xmbh,
        form_num: this.formNumDisplay || this.formNum,
        scope: this.scope,
        ntr_id: this.ntrId,
        skip_docx: 0,
        fields: JSON.stringify(merged)
      })
      const payload = res.data || {}
      if (payload.error) {
        if (!silent) this.$message.error(payload.error)
        return false
      }
      if (payload.document && this.ooConfig) {
        Object.assign(this.ooConfig.document, payload.document)
      }
      if (ed && typeof ed.reloadMergedDocument === 'function') {
        this.headerSynced = true
        const ok = await ed.reloadMergedDocument(this.ooConfig, '正在写入表头信息…')
        if (!ok && !silent) {
          this.headerSynced = false
          this.$message.error('Word 更新失败，请点「重新加载」')
          return false
        }
      }
      return true
    },
    onOoDocumentReady() {
      if (this.headerSynced || !this.showBatchToolbar) return
      const hasHeader = this.batchToolbarItems.some(item => {
        if (String(this.batchFields[item.key] || '').trim()) return true
        if (item.type === 'instrument' && item.siblings) {
          return Object.values(item.siblings).some(k => String(this.batchFields[k] || '').trim())
        }
        return false
      })
      if (!hasHeader) return
      this.mergeFieldsToWord(this.batchFields, { silent: true }).then(ok => {
        if (ok) this.headerSynced = true
      })
    },
    async applyFields(fields, options = {}) {
      const silent = !!(options && options.silent)
      this.applying = true
      try {
        const saved = await this.persistBatchFields(fields)
        if (!saved.ok) {
          if (!silent) this.$message.error(saved.error || '保存失败')
          return
        }
        if (!silent) {
          this.$message({
            message: '已保存',
            type: 'success',
            duration: 1200,
            showClose: false
          })
        }
      } catch (e) {
        if (!silent) this.$message.error((e && e.message) || '保存失败')
      } finally {
        this.applying = false
      }
    },
    onWordSaved() {
      if (this.saveSyncTimer) clearTimeout(this.saveSyncTimer)
      this.saveSyncTimer = setTimeout(() => this.syncAfterSave(), 3000)
    },
    async syncAfterSave() {
      const token = this.ooConfig && this.ooConfig.token
      if (!token) return
      try {
        const res = await api.get('yn_tr_lab_oo_sync', { token })
        const payload = res.data || {}
        if (payload.error) {
          this.$message.error(payload.error)
          return
        }
        await this.loadBatchFields()
        this.notifyOpener()
        const polish = payload.polish || {}
        const trimmed = Number(polish.trimmed_rows || 0)
        if (trimmed > 0 || polish.page_breaks_stripped) {
          const editor = this.$refs.ooEditor
          if (editor && typeof editor.reloadMergedDocument === 'function') {
            const cfg = await this.fetchOpenConfig()
            if (cfg) {
              await editor.reloadMergedDocument(cfg, '正在恢复单页排版…')
            }
          }
          this.$message({
            message: trimmed > 0
              ? `已自动裁掉 ${trimmed} 行多余样品行，请用续表录入更多样品`
              : '已去除手动分页符，恢复单页排版',
            type: 'warning',
            duration: 4000,
            showClose: true
          })
        } else if (polish.overflow) {
          this.$message({
            message: '主表行数仍超限，请改用续表（如 019-1）或点「清除数据并重填」',
            type: 'warning',
            duration: 5000,
            showClose: true
          })
        } else {
          this.$message({ message: '已保存', type: 'success', duration: 1200, showClose: false })
        }
      } catch (e) { /* ignore */ }
    },
    notifyOpener() {
      const msg = {
        type: 'yn_docx_synced',
        xmbh: this.xmbh,
        form_num: this.formNum,
        scope: this.scope
      }
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(msg, '*')
      }
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(msg, '*')
      }
    }
  }
}
</script>

<style scoped>
.docx-entry-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}
.docx-entry-page.is-embedded {
  height: 100%;
  min-height: 0;
}
.docx-entry-toolbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  background: #f5f7fa;
}
.toolbar-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}
.toolbar-select {
  width: 280px;
  max-width: 36vw;
}
.inst-opt-code {
  color: #409eff;
  font-size: 12px;
}
.toolbar-date {
  width: 148px;
}
.toolbar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.toolbar-reload {
  flex: 0 0 auto;
}
.docx-entry-editor {
  flex: 1 1 auto;
  min-height: 0;
}
.docx-entry-editor >>> .oo-editor-wrap {
  height: 100% !important;
  min-height: 0 !important;
  border: none;
}
.docx-entry-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 15px;
}
.docx-entry-placeholder.is-error {
  color: #f56c6c;
  padding: 24px;
  text-align: center;
}
</style>
